import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function Login() {
    const { loginWithGoogle, loginWithPhone, verifyOtp, linkPhoneToUser, setupRecaptcha, currentUser, dbUser } = useAuth();
    const navigate = useNavigate();

    // Phone Auth State
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const [linkingMode, setLinkingMode] = useState(false);

    React.useEffect(() => {
        if (currentUser && !linkingMode) {
            // If we logged in successfully and aren't waiting to link phone, check if we need to onboard
            // But if we just failed backend login due to missing phone, we shouldn't redirect yet
            // The backend login call inside loginWithGoogle would have thrown if it failed.
            // If loginWithGoogle succeeds, it means backend login succeeded (or we are just pure firebase authenticated)
            // Wait, our loginWithGoogle calls backend API. If that fails, it throws.
            // But existing useEffect redirects if currentUser provided. We need to prevent that if linking is needed.
            // Ideally we shouldn't have currentUser set if backend login failed? 
            // Firebase sets it automatically on success.
            // We can check if dbUser is loaded or if we are in linking mode.
        }
        if (currentUser && !linkingMode && !loading && dbUser) {
            // Check if user has RESTAURANT_ADMIN role
            const hasAdminRole = dbUser.roles && dbUser.roles.includes('RESTAURANT_ADMIN');

            if (!hasAdminRole) {
                // No admin role - redirect to onboarding to create restaurant
                navigate('/onboarding');
            } else if (dbUser.isNewUser) {
                navigate('/onboarding');
            } else {
                navigate('/dashboard');
            }
        }
    }, [currentUser, navigate, linkingMode, loading, dbUser]);

    const handleGoogleLogin = async () => {
        try {
            const user = await loginWithGoogle();
            const hasAdminRole = user.roles && user.roles.includes('RESTAURANT_ADMIN');

            if (!hasAdminRole) {
                navigate('/onboarding');
            } else if (user.isNewUser) {
                navigate('/onboarding');
            } else {
                navigate('/dashboard');
            }
        } catch (error) {
            if (error.response && error.response.status === 400 && error.response.data.error === "Phone number not found in token") {
                // Google Sign-In successful but needs phone - redirect to onboarding
                console.log("Google login successful, redirecting to onboarding for phone verification");
                navigate('/onboarding');
            } else if (error.code === 'auth/operation-not-allowed') {
                alert("Error: Google Sign-In is disabled. Enable it in Firebase Console.");
            } else {
                alert("Failed to login: " + error.message);
            }
        }
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            console.log('[Login] Setting up reCAPTCHA...');
            // Set to true to use visible reCAPTCHA - more reliable than invisible mode
            const useVisibleRecaptcha = true;
            setupRecaptcha('recaptcha-container', useVisibleRecaptcha);

            console.log('[Login] Sending OTP to:', phoneNumber);
            const appVerifier = window.recaptchaVerifier;
            const confirmation = await loginWithPhone(phoneNumber, appVerifier);
            setConfirmationResult(confirmation);
            setShowOtpInput(true);
            alert("OTP Sent! Check your phone.");
        } catch (error) {
            console.error('[Login] Error sending OTP:', error);

            // Display user-friendly error messages
            if (error.code === 'auth/operation-not-allowed') {
                alert("Error: Phone Auth is disabled. Enable it in Firebase Console.");
            } else if (error.code === 'auth/too-many-requests') {
                alert("Too many requests. Please try again later.");
            } else if (error.message.includes('reCAPTCHA')) {
                alert("reCAPTCHA Error: " + error.message + "\n\nTry refreshing the page.");
            } else {
                alert("Failed to send OTP: " + (error.message || error.code || 'Unknown error'));
            }
        }
        setLoading(false);
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            console.log('[Login] Verifying OTP...');
            let user;
            if (linkingMode && currentUser) {
                // Link phone to existing google user
                user = await linkPhoneToUser(currentUser, confirmationResult, otp);
                alert("Phone Linked & Login Complete!");
            } else {
                user = await verifyOtp(confirmationResult, otp);
            }

            const hasAdminRole = user.roles && user.roles.includes('RESTAURANT_ADMIN');

            if (!hasAdminRole) {
                navigate('/onboarding');
            } else if (user.isNewUser) {
                navigate('/onboarding');
            } else {
                navigate('/dashboard');
            }
        } catch (error) {
            console.error('[Login] OTP Verification Error:', error);

            if (error.code === 'auth/invalid-verification-code') {
                alert("Invalid OTP. Please check and try again.");
            } else if (error.code === 'auth/code-expired') {
                alert("OTP has expired. Please request a new one.");
            } else {
                alert("Verification Failed: " + (error.message || error.code || 'Unknown error'));
            }
        }
        setLoading(false);
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <h2 style={styles.title}>Admin Portal</h2>
                <p style={styles.subtitle}>Sign in to manage your restaurant</p>

                {/* Google Login */}
                <button onClick={handleGoogleLogin} style={styles.googleButton}>
                    Sign in with Google
                </button>

                <div style={styles.divider}>OR</div>

                {/* Phone Login */}
                {!showOtpInput ? (
                    <form onSubmit={handleSendOtp} style={styles.form}>
                        <input
                            type="tel"
                            placeholder="+1 555 555 5555"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            required
                            style={styles.input}
                        />
                        <div id="recaptcha-container"></div>
                        <button type="submit" disabled={loading} style={styles.button}>
                            {loading ? 'Sending...' : 'Send OTP'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleVerifyOtp} style={styles.form}>
                        <input
                            type="text"
                            placeholder="Enter OTP"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            required
                            style={styles.input}
                        />
                        <button type="submit" disabled={loading} style={styles.button}>
                            {loading ? 'Verifying...' : 'Verify OTP'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
    },
    card: {
        padding: '2rem',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        textAlign: 'center',
        width: '100%',
        maxWidth: '400px'
    },
    title: {
        marginBottom: '0.5rem',
        color: '#333'
    },
    subtitle: {
        marginBottom: '1.5rem',
        color: '#666'
    },
    googleButton: {
        width: '100%',
        padding: '0.75rem',
        backgroundColor: '#4285F4',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1rem',
        cursor: 'pointer',
        fontWeight: '500',
        marginBottom: '1rem'
    },
    divider: {
        margin: '1rem 0',
        color: '#aaa',
        fontSize: '0.9rem'
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
    },
    input: {
        padding: '0.75rem',
        borderRadius: '4px',
        border: '1px solid #ddd',
        fontSize: '1rem'
    },
    button: {
        width: '100%',
        padding: '0.75rem',
        backgroundColor: '#0F9D58',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1rem',
        cursor: 'pointer',
        fontWeight: '500'
    }
};
